import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AIEnhance from "@/components/AIEnhance";
import { pickAudioMime, extForMime } from "@/lib/audio";
import { Mic, Send, Play, Pause, Trash2, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "geral", l: "Mensagem geral" },
  { v: "atividade", l: "Sobre uma atividade" },
  { v: "material", l: "Sobre um material" },
  { v: "proxima_sessao", l: "Conversar na próxima sessão" },
  { v: "solicitacao_contato", l: "Solicitação de contato" },
  { v: "reagendamento", l: "Reagendamento" },
  { v: "apoio", l: "Preciso de apoio" },
];

function mmss(s) {
  s = Math.max(0, Math.round(s || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// Text + audio message composer (record → preview → transcript → send). Used by patient & counselor.
export default function MessageComposer({ patientId, enableAI = false, showCategory = false, onSent }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("geral");
  const [mode, setMode] = useState("idle"); // idle | recording | processing | preview
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [sending, setSending] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const mimeRef = useRef("");
  const urlRef = useRef(null);

  const resetAudio = () => {
    clearInterval(timerRef.current);
    if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch (e) {} urlRef.current = null; }
    setMode("idle"); setSeconds(0); setBlobUrl(null); setAudioFile(null);
    setTranscript(""); setTranscribing(false); setPlaying(false);
  };

  const sendText = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/messages", { patient_id: patientId, text, category, type: "text" });
      setText(""); setCategory("geral"); onSent?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mimeRef.current = mr.mimeType || mime || "";
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      mediaRef.current = mr;
      mr.start();
      setMode("recording"); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      console.error("[audio] getUserMedia failed", e);
      toast.error("Não foi possível acessar o microfone. Verifique a permissão do navegador.");
    }
  };

  const cancelRec = () => {
    clearInterval(timerRef.current);
    const mr = mediaRef.current;
    if (mr && mr.state !== "inactive") { mr.onstop = () => mr.stream.getTracks().forEach((t) => t.stop()); mr.stop(); }
    resetAudio();
  };

  const finalizeRec = () => {
    clearInterval(timerRef.current);
    const mr = mediaRef.current;
    if (!mr) return;
    const dur = seconds;
    setMode("processing");
    mr.onstop = async () => {
      mr.stream.getTracks().forEach((t) => t.stop());
      const type = mr.mimeType || mimeRef.current || "audio/webm";
      const ext = extForMime(type);
      const blob = new Blob(chunksRef.current, { type });
      if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch (e) {} }
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setBlobUrl(url);
      setSeconds(dur);
      console.info("[audio] recorded", { ua: navigator.userAgent, recMime: mimeRef.current, blobType: type, ext, size: blob.size, duration: dur });

      const fname = `mensagem.${ext}`;
      const upFd = new FormData(); upFd.append("file", blob, fname);
      let up = null;
      try {
        up = (await api.post("/files/upload", upFd, { headers: { "Content-Type": "multipart/form-data" } })).data;
        console.info("[audio] uploaded", { id: up && up.id, storage: up && up.storage_path, ctype: up && up.content_type, size: up && up.size });
      } catch (e) {
        console.error("[audio] upload failed", e);
        toast.error("Falha ao enviar o áudio. Tente novamente.");
        resetAudio(); return;
      }
      setAudioFile(up);
      setMode("preview");

      setTranscribing(true);
      const trFd = new FormData(); trFd.append("file", blob, fname);
      try {
        const tr = (await api.post("/ai/transcribe", trFd, { headers: { "Content-Type": "multipart/form-data" } })).data.text || "";
        setTranscript(tr);
        console.info("[audio] transcribed", { chars: tr.length });
      } catch (e) {
        console.error("[audio] transcription failed", e);
        toast.error("Não foi possível transcrever agora. Você ainda pode enviar o áudio.");
      } finally {
        setTranscribing(false);
      }
    };
    mr.stop();
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (playing) { a.pause(); return; }
      await a.play();
    } catch (e) {
      console.error("[audio] play failed", e);
      toast.error("Não foi possível reproduzir o áudio neste dispositivo.");
    }
  };

  const sendAudio = async () => {
    if (!audioFile) return;
    setSending(true);
    try {
      await api.post("/messages", {
        patient_id: patientId, type: "audio", audio_file_id: audioFile.id,
        transcript: transcript || null, duration: seconds, category,
      });
      resetAudio(); setCategory("geral"); onSent?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2">
      {showCategory && mode === "idle" && (
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="rounded-xl border-[#E0F2FE] h-9 text-sm" data-testid="message-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
        </Select>
      )}

      {enableAI && mode === "idle" && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#94A3B8]">A IA auxilia, você decide o que enviar.</span>
          <AIEnhance text={text} patientId={patientId} fieldType="mensagem ao paciente" onApply={setText} testid="ai-promessage" />
        </div>
      )}

      {mode === "idle" && (
        <div className="flex gap-2 items-end">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input"
            placeholder="Escreva ou fale…" className="rounded-xl border-[#E0F2FE] min-h-[46px] max-h-28" />
          <Button onClick={startRec} data-testid="record-audio-message-button" variant="outline"
            className="rounded-full h-12 w-12 p-0 shrink-0 border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E]/10">
            <Mic size={18} />
          </Button>
          <Button onClick={sendText} data-testid="send-message-button"
            className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] h-12 w-12 p-0 shrink-0"><Send size={18} /></Button>
        </div>
      )}

      {mode === "recording" && (
        <div className="flex items-center gap-3 rounded-2xl border border-[#E0F2FE] bg-[#F8FAFC] px-3 py-2" data-testid="recording-ui">
          <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444] animate-soft-pulse shrink-0" />
          <div className="flex items-end gap-[3px] h-6">
            {[...Array(10)].map((_, i) => (
              <span key={i} className="w-[3px] rounded-full bg-[#0F766E] animate-soft-pulse"
                style={{ height: `${8 + ((i * 5 + seconds * 4) % 20)}px`, animationDelay: `${i * 80}ms` }} />
            ))}
          </div>
          <span className="text-sm font-semibold text-[#1F3B4D] tabular-nums" data-testid="recording-timer">{mmss(seconds)}</span>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={cancelRec} data-testid="cancel-recording" className="text-[#94A3B8]">Cancelar</Button>
          <Button size="sm" onClick={finalizeRec} data-testid="finish-recording" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259]">Finalizar</Button>
        </div>
      )}

      {mode === "processing" && (
        <div className="flex items-center gap-3 rounded-2xl border border-[#E0F2FE] bg-[#F8FAFC] px-4 py-3" data-testid="audio-processing">
          <Loader2 size={18} className="animate-spin text-[#0F766E]" />
          <span className="text-sm text-[#1F3B4D]">Preparando o áudio…</span>
        </div>
      )}

      {mode === "preview" && (
        <div className="rounded-2xl border border-[#E0F2FE] bg-[#F8FAFC] p-3 space-y-3" data-testid="audio-preview">
          <div className="flex items-center gap-3">
            <Button onClick={togglePlay} data-testid="preview-play"
              className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] h-10 w-10 p-0 shrink-0">
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </Button>
            <div className="flex items-end gap-[3px] h-6 flex-1">
              {[10, 16, 22, 14, 18, 11, 20, 13, 9, 17].map((h, i) => <span key={i} className="w-[3px] rounded-full bg-[#0F766E]/50" style={{ height: `${h}px` }} />)}
            </div>
            <span className="text-sm text-[#1F3B4D] tabular-nums">{mmss(seconds)}</span>
            <Button variant="ghost" size="icon" onClick={resetAudio} data-testid="delete-audio" title="Excluir"><Trash2 size={16} className="text-[#EF4444]" /></Button>
            <Button variant="ghost" size="icon" onClick={startRec} data-testid="rerecord-audio" title="Gravar novamente"><RotateCcw size={16} className="text-[#60A5FA]" /></Button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#0F766E]">Transcrição</label>
              {transcribing
                ? <span className="text-xs text-[#94A3B8] flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Transcrevendo áudio…</span>
                : (enableAI && <AIEnhance text={transcript} patientId={patientId} fieldType="mensagem ao paciente" onApply={setTranscript} testid="ai-audio-transcript" />)}
            </div>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="audio-transcript"
              placeholder="A transcrição aparecerá aqui. Você pode revisar, corrigir ou complementar." className="rounded-xl border-[#E0F2FE] min-h-[70px]" />
          </div>

          <Button onClick={sendAudio} disabled={sending} data-testid="send-audio-message"
            className="w-full rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Enviar áudio
          </Button>

          {blobUrl && <audio ref={audioRef} src={blobUrl} className="hidden"
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />}
        </div>
      )}
    </div>
  );
}
